import React, { useEffect, useState } from 'react';
import './LandingPage.css';
import Navbar from './components/ui/Navbar';
import HeroSection from './components/HeroSection';
import StatsSection from './components/StatsSection';
import ProductionSourcingSection from './components/ProductionSourcingSection';
import VerifiedCreatorsSection from './components/VerifiedCreatorsSection';
import PathCardsSection from './components/PathCardsSection';
import IdeasBackerSection from './components/IdeasBackerSection';
import IndustryEducationSection from './components/IndustryEducationSection';
import RealOutcomesSection from './components/RealOutcomesSection';
import SplitCtaSection from './components/SplitCtaSection';
import PillFooter from './components/PillFooter';
import { useNavigate } from 'react-router-dom';
import { getTalentPoolPath, isProducerLikeRole } from '../utils/talentPoolNavigation';

const segments = [
  {
    title: 'Learning & Certification',
    description: 'Access courses, upskill and get certified in new domains.',
    features: ['Skill', 'Upskill', 'Reskill'],
  },
  {
    title: 'Talent pool',
    description: 'Create professional profiles showcasing subject-matter expertise.',
    features: ['Standard & Premium features', 'Portfolio Builder', 'Visibility Tools'],
  },
  {
    title: 'Sourcing',
    description: 'Find talent, equipment, and locations for production needs.',
    features: ['Studios', 'Equipment Rental', 'Indoor/Outdoor Locations'],
  },
  {
    title: 'Pitching',
    description: 'Upload proposals and connect with producers and marketers.',
    features: ['Pitch Templates', 'OTT & Theatre Connections', 'Submit Decks'],
  },
  {
    title: 'Screening Stories',
    description: 'Get feedback from writers & AI tools on story viability.',
    features: ['AI ROI Analysis', 'Writer Panel Feedbacks', 'Genre Targeting'],
  },
  {
    title: 'Funding',
    description: 'Connect with investors, producers for project funding.',
    features: ['Investor Listings', 'Submit Projects', 'Chat with Backers'],
  },
];

const LandingPage = () => {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // Check login status from localStorage
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsLoggedIn(true);
      } catch (err) {
        setUser(null);
        setIsLoggedIn(false);
      }
    } else {
      setUser(null);
      setIsLoggedIn(false);
    }
  }, []);

  const handleLogout = () => {
    // Clear auth data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsLoggedIn(false);
    setMessage('You have been logged out.');
    nav('/');
  };

  const requireLogin = (cb) => {
    if (!isLoggedIn) {
      setMessage('Please login to access this feature.');
      nav('/login');
      return;
    }
    if (typeof cb === 'function') {
      cb();
    }
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return (
      user.name ||
      user.fullName ||
      user.firstName ||
      user.username ||
      user.email ||
      ''
    );
  };

  const getSplitCtaAudience = () => {
    if (!isLoggedIn || !user?.userType) return 'all';

    const userType = String(user.userType).toLowerCase();
    if (userType === 'talent' || userType === 'creator') return 'creator';
    if (userType === 'producer' || userType === 'recruiter' || userType === 'investor') {
      return 'producer';
    }
    return 'all';
  };

  return (
    <div className="landing-container">
      <header className="hero">
        {/* Top navigation bar */}
        <Navbar
          isLoggedIn={isLoggedIn}
          displayName={getUserDisplayName()}
          onLogout={handleLogout}
          onNavigate={nav}
        />

        {/* Logo strip (kept from previous design) */}
        <div className="logo-container">
          <div className="logo-left">
            <img src="/p3.jpg" alt="Cinepreneur Logo" className="cinepreneur-logo" />
          </div>
          <div className="logo-right">
            <img src="/p1.jpg" alt="MeeSchool Logo" className="meeschool-logo" />
          </div>
        </div>

        {/* Hero content matching new copy (Tailwind) */}
        <HeroSection onNavigate={nav} audience={getSplitCtaAudience()} />
      </header>
      <StatsSection />
      <ProductionSourcingSection onViewAll={() => nav('/production-sourcing')} />
      <VerifiedCreatorsSection onBrowseAll={() => nav('/certified-professional')} />
      <PathCardsSection onNavigate={nav} user={user} />
      <IdeasBackerSection onSignup={() => nav('/register')} />
      <IndustryEducationSection
        onViewAll={() => nav('/learning-certification')}
        onEnroll={() => nav('/learning-certification')}
      />
      <RealOutcomesSection />
      <SplitCtaSection
        audience={getSplitCtaAudience()}
        onCreator={() => nav(getTalentPoolPath(user?.userType))}
        onHire={() => nav('/profiles')}
      />

      <section className="segments">
        {segments.map((segment, index) => {
          const isPitching = segment.title === 'Pitching';
          const isProfiling = segment.title === 'Talent pool';

          const isLearning = segment.title === 'Learning & Certification';
          const isSourcing = segment.title === 'Sourcing';
          const isFunding = segment.title === 'Funding';

          const handleDoubleClick = () => {
            if (isPitching) {
              requireLogin(() => {
                // Check if user is talent
                const storedUser = localStorage.getItem('user');
                if (!storedUser) {
                  nav('/login', { state: { from: '/pitch-upload' } });
                  return;
                }
                try {
                  const userData = JSON.parse(storedUser);
                  if (userData.userType !== 'talent') {
                    setMessage('Only talent users can upload pitches. Recruiters can view pitches.');
                    nav('/producer-pitches');
                    return;
                  }
                  nav('/pitch-upload');
                } catch (err) {
                  nav('/login');
                }
              });
            } else if (isProfiling) {
              // Profiling: no login required; producers/recruiters/investors → all profiles
              const storedUser = localStorage.getItem('user');
              if (storedUser) {
                try {
                  const userData = JSON.parse(storedUser);
                  if (isProducerLikeRole(userData.userType)) {
                    nav('/profiles');
                    return;
                  }
                } catch (err) {
                  // ignore
                }
              }
              nav('/profile');
            } else if (isSourcing) {
              nav('/production-sourcing');
            } else if (isLearning) {
              requireLogin(() => {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                  try {
                              JSON.parse(storedUser);
                    nav('/learning-certification');
                  } catch (err) {
                    nav('/login');
                  }
                } else {
                  nav('/login', { state: { from: '/learning-certification' } });
                }
              });
            } else if (isFunding) {
              requireLogin(() => {
                nav('/funding-projects');
              });
            } else {
              requireLogin();
            }
          };

          return (
            <div
              key={index}
              className="segment-card"
              onClick={handleDoubleClick}
            >
              <h2>{segment.title === 'Talent pool' && user?.userType === 'talent' ? 'Profiling' : segment.title}</h2>
              <p>{segment.description}</p>
              {isLearning ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                  {segment.features.map((item, i) => (
                    <button
                      key={i}
                      className="deep-dive"
                      onClick={(e) => {
                        e.stopPropagation();
                        requireLogin(() => {
                          const storedUser = localStorage.getItem('user');
                          if (storedUser) {
                            try {
                              JSON.parse(storedUser);
                              const section = item.toLowerCase();
                              nav(`/learning-certification?section=${section}`);
                            } catch (err) {
                              nav('/login');
                            }
                          } else {
                            nav('/login', { state: { from: '/learning-certification' } });
                          }
                        });
                      }}
                      style={{ width: '100%', textAlign: 'center' }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              ) : (
                <ul>
                  {segment.features.map((item, i) => (
                    <li key={i}>✔️ {item}</li>
                  ))}
                </ul>
              )}
              {isPitching && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <button
                    className="deep-dive"
                    onClick={(e) => {
                      e.stopPropagation();
                      requireLogin(() => {
                        const storedUser = localStorage.getItem('user');
                        if (!storedUser) {
                          nav('/login', { state: { from: '/pitch-upload' } });
                          return;
                        }
                        try {
                          const userData = JSON.parse(storedUser);
                          if (userData.userType !== 'talent') {
                            setMessage('Only talent users can upload pitches. Recruiters can view pitches.');
                            nav('/producer-pitches');
                            return;
                          }
                          nav('/pitch-upload');
                        } catch (err) {
                          nav('/login');
                        }
                      });
                    }}
                  >
                    Upload Pitch
                  </button>
                  <button
                    className="deep-dive"
                    onClick={(e) => {
                      e.stopPropagation();
                      requireLogin(() => {
                        nav('/funding-projects');
                      });
                    }}
                  >
                    View All Pitches
                  </button>
                </div>
              )}
              {isProfiling && (
                <button
                  className="deep-dive"
                  onClick={(e) => {
                    e.stopPropagation();
                    nav(getTalentPoolPath(user?.userType));
                  }}
                >
                  {isProducerLikeRole(user?.userType) ? 'View all profiles' : 'Create/Edit Profile'}
                </button>
              )}
              {!isPitching && !isProfiling && !isLearning && (
                <button
                  className="deep-dive"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSourcing) {
                      nav('/production-sourcing');
                    } else if (isFunding) {
                      requireLogin(() => {
                        nav('/producer-pitches');
                      });
                    } else {
                      requireLogin();
                    }
                  }}
                >
                  Explore More
                </button>
              )}
            </div>
          );
        })}
      </section>

      <section className="public-registration-cta">
        <h2>Team Registration</h2>
        <p>This form is open to all and includes payment-based submission.</p>
        <button className="deep-dive" onClick={() => nav('/team-registration')}>
          Open Registration Page
        </button>
      </section>

      <PillFooter onNavigate={nav} />

      {message != null && (
        <div className="landing-message-overlay" onClick={() => setMessage(null)}>
          <div className="landing-message-box" onClick={(e) => e.stopPropagation()}>
            <p className="landing-message-text">{message}</p>
            <button
              type="button"
              className="landing-message-ok"
              onClick={() => setMessage(null)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
